/**
 * KK-1210 / KK-1211 / KK-1212 / KK-1213 / KK-1214 · Yükleme onayı.
 *
 * MVP'nin en çok sürtünme üreten ekranı. Sanal POS v-next'e ertelendiği için
 * veli parayı okulun IBAN'ına gönderir ve bakiyenin yansıması için OKUL
 * PERSONELİNİN ONAYINI bekler. Gecikme doğrudan "çocuğum aç kaldı" şikayetine
 * döner (H-06) ve H-07 hedefi medyan onay süresini 4 saatin altında tutmaktır.
 *
 * Bu yüzden ekranın tasarım kararı şudur: KUYRUK YAŞLANMAYA GÖRE SIRALANIR ve
 * gecikme rakamı sayfanın en üstünde durur. Ölçülmeyen süre kısalmaz.
 *
 * KK-1214: kasiyer bu ekranı göremez. Yetkisiz kullanıcıya boş tablo değil,
 * ne yapması gerektiğini söyleyen bir mesaj gösterilir.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Avatar,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { IstatistikKarosu } from "@kantin/ui/grafik";
import {
  HareketTipi,
  KartDurumu,
  YuklemeBildirimDurumu,
  para,
  paraGoster,
} from "@kantin/shared";
import { ApiHatasi, apiOku, apiYaz, yetkisizMi } from "./api.js";
import { useNoktalar } from "./kancalar.js";
import { YuklemeGeriAl } from "./YuklemeGeriAl.js";
import type { GeriAlinabilirYukleme } from "./YuklemeGeriAl.js";
import { NakitNoktasiSecimi, nakitNoktasiUygunMu } from "./NakitNoktasiSecimi.js";
import { useMedyaGoruntusu } from "../../veri/medya.js";
import {
  IslemDurumu,
  MetinAlani,
  OnayKutusu,
  Sayfa,
  SayfaBasligi,
  Sekmeler,
  SorguDurumu,
  TehlikeliOnay,
  bugun,
  hataMetni,
  paraGecerliMi,
  paraNormalize,
  saatMetni,
  tarihGoster,
  tarihSaatGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------

interface KuyrukSatiri extends GeriAlinabilirYukleme {
  id: string;
  kisi_id: string;
  kisi_ad: string;
  sinif: string | null;
  yukleme_referansi: string;
  veli_id: string | null;
  veli_ad: string | null;
  tutar: string;
  havale_tarihi: string;
  /** 0015 öncesi bildirimlerin serbest metin adresi. */
  dekont_url: string | null;
  dekont_medya_id: string | null;
  aciklama: string | null;
  durum: number;
  durum_adi: string;
  red_gerekcesi: string | null;
  hareket_id: string | null;
  onaylayan_id: string | null;
  onaylayan_ad: string | null;
  karar_zamani: string | null;
  olusma_zamani: string;
  bekleme_saati: string;
  yaslanma: "yeni" | "bekliyor" | "gecikti" | "karar_verildi";
}

interface KuyrukYaniti {
  sayfa: number;
  boyut: number;
  kayitlar: KuyrukSatiri[];
  ozet: {
    bekleyen_adet: number;
    geciken_adet: number;
    en_eski_bekleme_saati: string | null;
    medyan_onay_saati: string | null;
    hedef_saat: number;
  };
}

interface ReferansYaniti {
  kisi_id: string;
  ad: string;
  soyad: string;
  sinif: string | null;
  numara: string | null;
  yukleme_referansi: string;
  aktif: boolean;
  bakiye: string | null;
  veliler: { ad: string; soyad: string }[];
  bekleyen_bildirimler: {
    id: string;
    tutar: string;
    havale_tarihi: string;
    olusma_zamani: string;
  }[];
}

/** GET /yukleme/ogrenci — seçim için gereken alanlar; ayrıntı referansla gelir. */
interface OgrenciAdayi {
  kisi_id: string;
  ad: string;
  soyad: string;
  sinif: string | null;
  numara: string | null;
  yukleme_referansi: string;
  aktif: boolean;
  /** Yalnızca kartla aramada dolu; aktif değilse UI uyarır. */
  kart_durumu: number | null;
}

interface OgrenciAramaYaniti {
  veri: OgrenciAdayi[];
  kesildi: boolean;
}

type OgrenciSorgusu = { arama: string } | { kart_uid: string };

type Sekme = "bekleyen" | "dogrudan" | "kararlar";

export function YuklemeOnay() {
  const [sekme, setSekme] = useState<Sekme>("bekleyen");

  const kuyruk = useQuery({
    queryKey: ["yukleme-bildirim", "bekleyen"],
    queryFn: () =>
      apiOku<KuyrukYaniti>("/yukleme/bildirim", {
        durum: YuklemeBildirimDurumu.ONAY_BEKLIYOR,
        boyut: 100,
      }),
    // Kuyruk yaşlanır; ekran açık kaldığı sürece tazelenmelidir.
    refetchInterval: 60_000,
  });

  // KK-1214: kasiyer bu ekranı göremez.
  if (kuyruk.error && yetkisizMi(kuyruk.error)) {
    return (
      <Sayfa>
        <SayfaBasligi baslik="Yükleme onayı" />
        <Uyari tur="hata" baslik="Bu ekranı görüntüleme yetkiniz yok">
          <p className="m-0">
            Bakiye yükleme (havale onayı ve nakit tahsilat) yalnızca okul
            yönetimi, okul sistem yöneticisi ve mali müşavir tarafından
            yapılabilir. Kantin müdürü, nokta görevlisi ve kasiyer bu işlemi
            yapamaz.
          </p>
          <p className="m-0 mt-2">
            Yetki değişikliği için okul yönetimine başvurun.
          </p>
        </Uyari>
      </Sayfa>
    );
  }

  const ozet = kuyruk.data?.ozet;

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Yükleme onayı"
        aciklama="Velinin havale bildirimleri, doğrudan havale işleme ve elden nakit yükleme. Onay anında bakiye yansır."
      />

      {/* H-07 — hedef ekranın üstünde durur, ölçülmeyen süre kısalmaz. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <IstatistikKarosu
          etiket="Bekleyen bildirim"
          deger={
            <span className="rakam font-mono">{ozet?.bekleyen_adet ?? 0}</span>
          }
        />
        <IstatistikKarosu
          etiket="4 saati aşan"
          deger={
            <span className="rakam font-mono">{ozet?.geciken_adet ?? 0}</span>
          }
          yardimci="H-07 eşiği"
          vurgu={(ozet?.geciken_adet ?? 0) > 0}
        />
        <IstatistikKarosu
          etiket="En uzun bekleyen"
          deger={saatMetni(ozet?.en_eski_bekleme_saati)}
        />
        <IstatistikKarosu
          etiket="Medyan onay süresi"
          deger={saatMetni(ozet?.medyan_onay_saati)}
          yardimci={`Hedef: ${ozet?.hedef_saat ?? 4} saatin altında`}
        />
      </div>

      <Sekmeler<Sekme>
        etiket="Yükleme bölümleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          {
            deger: "bekleyen",
            metin: "Bekleyen bildirimler",
            sayi: ozet?.bekleyen_adet ?? 0,
          },
          { deger: "dogrudan", metin: "Doğrudan yükleme" },
          { deger: "kararlar", metin: "Verilen kararlar" },
        ]}
      />

      {sekme === "bekleyen" && <BekleyenKuyruk sorgu={kuyruk} />}
      {sekme === "dogrudan" && <DogrudanYukleme />}
      {sekme === "kararlar" && <VerilenKararlar />}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

const YASLANMA_ROZETI: Record<
  KuyrukSatiri["yaslanma"],
  { tur: "notr" | "bilgi" | "uyari" | "hata"; metin: string }
> = {
  yeni: { tur: "bilgi", metin: "yeni" },
  bekliyor: { tur: "uyari", metin: "bekliyor" },
  gecikti: { tur: "hata", metin: "4 saati aştı" },
  karar_verildi: { tur: "notr", metin: "karar verildi" },
};

function BekleyenKuyruk({
  sorgu,
}: {
  sorgu: {
    isPending: boolean;
    error: unknown;
    data: KuyrukYaniti | undefined;
    refetch: () => unknown;
  };
}) {
  const kayitlar = sorgu.data?.kayitlar ?? [];
  return (
    <Kart
      baslik="Bekleyen bildirimler"
      ustBilgi={
        <span className="text-sm text-murekkep-3">
          En çok bekleyen en üstte
        </span>
      }
    >
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={kayitlar.length === 0}
        bosBaslik="Bekleyen bildirim yok"
        bosAciklama="Tüm veli bildirimleri sonuçlandırılmış. Ekstrede gördüğünüz bir havaleyi 'Doğrudan yükleme' sekmesinden işleyebilirsiniz."
        yenile={() => void sorgu.refetch()}
      >
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {kayitlar.map((k) => (
            <BildirimSatiri key={k.id} kayit={k} />
          ))}
        </ul>
      </SorguDurumu>
    </Kart>
  );
}

function BildirimSatiri({ kayit }: { kayit: KuyrukSatiri }) {
  const istemci = useQueryClient();
  const [onayAcik, setOnayAcik] = useState(false);
  const [redAcik, setRedAcik] = useState(false);
  const [gerekce, setGerekce] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const tazele = () => {
    void istemci.invalidateQueries({ queryKey: ["yukleme-bildirim"] });
    void istemci.invalidateQueries({ queryKey: ["kisi"] });
    // Kenar çubuğu rozeti gösterge özetinden okur (sorgular.ts useOzet).
    void istemci.invalidateQueries({ queryKey: ["m8", "ozet"] });
  };

  // Onay anında para hareketi oluşur:
  // tek tıkla tetiklenmez, İmha/Sayım'daki TehlikeliOnay kalıbıyla ikinci
  // adımda "kim, ne kadar" tekrar gösterilip onaylanır.
  const onayla = useMutation({
    mutationFn: () =>
      apiYaz<{ yeni_bakiye: string }>(
        "POST",
        `/yukleme/bildirim/${kayit.id}/onayla`,
      ),
    onSuccess: () => {
      setOnayAcik(false);
      tazele();
    },
  });

  const reddet = useMutation({
    mutationFn: () =>
      apiYaz("POST", `/yukleme/bildirim/${kayit.id}/reddet`, {
        gerekce: gerekce.trim(),
      }),
    onSuccess: () => {
      setRedAcik(false);
      setGerekce("");
      setGonderildi(false);
      tazele();
    },
  });

  const rozet = YASLANMA_ROZETI[kayit.yaslanma];
  const gecikti = kayit.yaslanma === "gecikti";
  const [ad = "", ...kalan] = kayit.kisi_ad.split(" ");
  const soyad = kalan.join(" ");

  const gerekceHatasi =
    gonderildi && gerekce.trim().length < 5
      ? "Ret gerekçesi en az 5 karakter olmalıdır; gerekçe veliye bildirilir."
      : undefined;

  return (
    <li
      className={`rounded-md border p-3 ${
        gecikti ? "border-hata-cizgi bg-hata-yumusak" : "border-cetvel bg-yuzey"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Avatar ad={ad} soyad={soyad || ad} boyut={40} />
          <div>
            <p className="m-0 font-semibold text-murekkep">{kayit.kisi_ad}</p>
            <p className="m-0 text-xs text-murekkep-3">
              {kayit.sinif ?? "—"} · referans{" "}
              <span className="rakam font-mono">{kayit.yukleme_referansi}</span>
            </p>
            <p className="m-0 text-xs text-murekkep-3">
              Havale tarihi: {tarihGoster(kayit.havale_tarihi)}
              {kayit.veli_ad
                ? ` · bildiren: ${kayit.veli_ad}`
                : " · veli bildirimi yok"}
            </p>
            {kayit.aciklama && (
              <p className="m-0 mt-1 text-sm text-murekkep-2">
                {kayit.aciklama}
              </p>
            )}
          </div>
        </div>

        <div className="text-right">
          <Tutar
            deger={kayit.tutar}
            yonsuz
            boy="xl"
            etiket="bildirilen tutar"
          />
          <p className="m-0 mt-1 flex flex-wrap items-center justify-end gap-2">
            <Rozet tur={rozet.tur}>{rozet.metin}</Rozet>
            <span className="rakam font-mono text-xs text-murekkep-3">
              {saatMetni(kayit.bekleme_saati)} bekliyor
            </span>
          </p>
          <p className="m-0 text-xs text-murekkep-3">
            Bildirim: {tarihSaatGoster(kayit.olusma_zamani)}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-cetvel pt-3">
        <DekontBagi kayit={kayit} />
        <Buton
          tur="onay"
          onClick={() => {
            setRedAcik(false);
            setOnayAcik((a) => !a);
          }}
          aria-expanded={onayAcik}
          disabled={onayla.isSuccess}
        >
          Onayla — bakiyeye yansıt
        </Buton>
        <Buton
          tur="tehlike"
          onClick={() => {
            setOnayAcik(false);
            setRedAcik((a) => !a);
          }}
          aria-expanded={redAcik}
          disabled={onayla.isSuccess}
        >
          Reddet
        </Buton>
      </div>

      {onayAcik && !onayla.isSuccess && (
        <div className="mt-3">
          <TehlikeliOnay
            soru={`${kayit.kisi_ad} bakiyesine ${paraGoster(kayit.tutar)} yansıtılsın mı?`}
            aciklama={
              <>
                Bakiye anında artar ve veliye bildirim
                gider. Dekonttaki tutar ile bildirilen tutarın (
                <Tutar deger={kayit.tutar} yonsuz boy="sm" />) uyuştuğundan emin
                olun; para gelmediyse &quot;Reddet&quot; kullanın.
              </>
            }
            onayMetni="Evet, bakiyeye yansıt"
            onayla={() => onayla.mutate()}
            vazgec={() => setOnayAcik(false)}
            yukleniyor={onayla.isPending}
          />
        </div>
      )}

      <IslemDurumu hata={onayla.error} />
      {onayla.data && (
        <Uyari tur="onay" baslik="Yükleme onaylandı">
          Yeni bakiye:{" "}
          <Tutar
            deger={onayla.data.yeni_bakiye}
            yonsuz
            boy="sm"
            etiket="yeni bakiye"
          />
        </Uyari>
      )}

      {redAcik && (
        <form
          className="mt-3 flex flex-col gap-2 rounded-lg border border-hata-cizgi bg-hata-yumusak p-3"
          onSubmit={(e) => {
            e.preventDefault();
            setGonderildi(true);
            if (gerekce.trim().length < 5) return;
            reddet.mutate();
          }}
        >
          <MetinAlani
            etiket="Ret gerekçesi (zorunlu)"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            rows={2}
            ipucu="Gerekçe veliye aynen bildirilir. Gerekçesiz ret, veliyi telefonla okulu aramaya iter."
            {...(gerekceHatasi ? { hata: gerekceHatasi } : {})}
          />
          <IslemDurumu hata={reddet.error} />
          <div className="flex gap-2">
            <Buton type="submit" tur="tehlike" yukleniyor={reddet.isPending}>
              Bildirimi reddet
            </Buton>
            <Buton
              type="button"
              tur="ikincil"
              onClick={() => setRedAcik(false)}
            >
              Vazgeç
            </Buton>
          </div>
        </form>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// KK-1211 / KK-1212 · Doğrudan yükleme
// ---------------------------------------------------------------------------

function DogrudanYukleme() {
  const istemci = useQueryClient();
  const noktalar = useNoktalar({ aktif: true });

  const [tip, setTip] = useState<"havale" | "nakit">("havale");
  const [metin, setMetin] = useState("");
  const [kartUid, setKartUid] = useState("");
  /** Son gönderilen arama; adaylar bundan gelir. */
  const [sorgu, setSorgu] = useState<OgrenciSorgusu | null>(null);
  /** Seçilen öğrencinin referans kodu; ayrıntı bununla okunur. */
  const [aranan, setAranan] = useState("");
  const [tutar, setTutar] = useState("");
  const [havaleTarihi, setHavaleTarihi] = useState(bugun());
  const [noktaId, setNoktaId] = useState("");
  const [bildirimId, setBildirimId] = useState<string | null>(null);
  const [aciklama, setAciklama] = useState("");
  const [tekrarOnayi, setTekrarOnayi] = useState(false);
  const [gonderildi, setGonderildi] = useState(false);
  /** İkinci adım: "kim, ne kadar, nereye" özeti onaylanmadan hareket yazılmaz. */
  const [onayAcik, setOnayAcik] = useState(false);

  const adaylar = useQuery({
    queryKey: ["yukleme-ogrenci", sorgu],
    queryFn: () => apiOku<OgrenciAramaYaniti>("/yukleme/ogrenci", sorgu!),
    enabled: sorgu !== null,
    retry: false,
  });

  // Tek aday varsa seçim adımı atlanır: kart okutan ya da tam referans/numara
  // yazan personel bir de listeden tıklamak zorunda kalmaz.
  const adayListesi = adaylar.data?.veri;
  useEffect(() => {
    if (adayListesi?.length === 1) setAranan(adayListesi[0]!.yukleme_referansi);
  }, [adayListesi]);

  const referans = useQuery({
    queryKey: ["yukleme-referans", aranan],
    queryFn: () =>
      apiOku<ReferansYaniti>(`/yukleme/referans/${encodeURIComponent(aranan)}`),
    enabled: aranan.trim().length >= 3,
    retry: false,
  });

  const isle = useMutation({
    mutationFn: () => {
      if (tip === "nakit" && !seciliNokta) {
        throw new Error("Nakit tahsilat için kantin olmayan aktif bir nokta seçin.");
      }
      return apiYaz<{
        kisi_ad: string;
        tutar: string;
        yeni_bakiye: string;
        tip_adi: string;
        uyari?: string;
      }>("POST", "/yukleme/isle", {
        kisi_id: referans.data?.kisi_id,
        tutar: paraNormalize(tutar),
        tip:
          tip === "nakit"
            ? HareketTipi.YUKLEME_NAKIT
            : HareketTipi.YUKLEME_HAVALE,
        ...(bildirimId ? { bildirim_id: bildirimId } : {}),
        ...(tip === "nakit" && noktaId ? { nokta_id: noktaId } : {}),
        ...(tip === "havale" ? { havale_tarihi: havaleTarihi } : {}),
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
        ...(tekrarOnayi ? { tekrar_onayi: true } : {}),
      });
    },
    onSuccess: () => {
      setOnayAcik(false);
      setTutar("");
      setBildirimId(null);
      setAciklama("");
      setTekrarOnayi(false);
      setGonderildi(false);
      void istemci.invalidateQueries({ queryKey: ["yukleme-bildirim"] });
      void istemci.invalidateQueries({ queryKey: ["yukleme-referans"] });
      void istemci.invalidateQueries({ queryKey: ["kisi"] });
      void istemci.invalidateQueries({ queryKey: ["m8", "ozet"] });
    },
  });

  const kisi = referans.data;
  const seciliAday = adayListesi?.find((a) => a.yukleme_referansi === aranan);
  const kartUyarisi =
    seciliAday?.kart_durumu != null &&
    seciliAday.kart_durumu !== KartDurumu.AKTIF;
  const tutarHatasi =
    gonderildi && !paraGecerliMi(tutar)
      ? "Tutarı 50 veya 50,00 biçiminde girin."
      : undefined;
  const mukerrerUyarisi =
    isle.error instanceof ApiHatasi && isle.error.kod === "CAKISMA";
  // Nokta eksikse sunucu zaten reddeder; ama onay diyaloğuna "nereye" bilgisi
  // olmadan gitmek anlamsızdır — burada durdurulur.
  const seciliNokta = noktalar.data?.noktalar.find(
    (n) => n.id === noktaId && nakitNoktasiUygunMu(n),
  );
  const noktaHatasi =
    gonderildi && tip === "nakit" && !seciliNokta
      ? "Nakit tahsilatın alındığı noktayı seçin."
      : undefined;
  const beklenenBakiye =
    kisi?.bakiye && paraGecerliMi(tutar)
      ? paraGoster(para(kisi.bakiye).plus(para(paraNormalize(tutar))))
      : null;

  return (
    <>
      <Uyari
        tur="bilgi"
        baslik="Veli bildirimi bir kolaylıktır, zorunluluk değil"
      >
        Ekstrede gördüğünüz bir havaleyi referans kodundan, ad/okul
        numarasından ya da kartı okutarak öğrenciyi bulup doğrudan
        işleyebilirsiniz. Elden alınan nakit de aynı yerden işlenir ve gün sonu
        sayımına girer.
      </Uyari>

      <Kart baslik="1 · Öğrenciyi bul">
        <div className="grid gap-3 md:grid-cols-2">
          <form
            className="grid gap-3 md:grid-cols-[1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              const a = metin.trim();
              if (a.length < 2) return;
              setSorgu({ arama: a });
              setAranan("");
              setBildirimId(null);
            }}
          >
            <Alan
              etiket="Referans kodu, ad soyad veya okul no"
              value={metin}
              onChange={(e) => setMetin(e.target.value)}
              ipucu="Ekstredeki kod (KNT-4821), Ayşe Yılmaz ya da 1042. Büyük/küçük harf farkı önemli değildir."
              autoComplete="off"
            />
            <div className="flex items-end">
              <Buton type="submit" tur="birincil">
                Ara
              </Buton>
            </div>
          </form>

          <KartOkutmaAlani
            deger={kartUid}
            degistir={setKartUid}
            okut={(uid) => {
              setSorgu({ kart_uid: uid });
              setAranan("");
              setBildirimId(null);
            }}
          />
        </div>

        {sorgu !== null && !aranan && (
          <div className="mt-3">
            <SorguDurumu
              yukleniyor={adaylar.isPending}
              hata={adaylar.error}
              bos={adayListesi?.length === 0}
              bosBaslik="Öğrenci bulunamadı"
              bosAciklama={
                "kart_uid" in sorgu
                  ? "Bu karta bağlı bir öğrenci yok. Kart eşleştirmesini Kartlar ekranından yapın."
                  : "Ad, okul numarası veya referans kodunu kontrol edin."
              }
              iskeletSatir={2}
              yenile={() => void adaylar.refetch()}
            >
              {adayListesi && adayListesi.length > 1 && (
                <div>
                  <p className="m-0 mb-2 text-sm font-semibold text-murekkep-2">
                    Birden fazla öğrenci eşleşti — doğrusunu seçin
                  </p>
                  <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {adayListesi.map((a) => (
                      <li key={a.kisi_id}>
                        <button
                          type="button"
                          onClick={() => setAranan(a.yukleme_referansi)}
                          className="flex min-h-hedef w-full items-center justify-between gap-3 rounded-md border border-cetvel bg-yuzey px-3 text-left hover:bg-kagit-3"
                        >
                          <span className="flex items-center gap-2">
                            <Avatar ad={a.ad} soyad={a.soyad} boyut={28} />
                            <span className="text-sm text-murekkep">
                              {a.ad} {a.soyad}
                            </span>
                            {!a.aktif && <Rozet tur="uyari">pasif</Rozet>}
                          </span>
                          <span className="text-xs text-murekkep-3">
                            {a.sinif ?? "—"}
                            {a.numara ? ` · No ${a.numara}` : ""} ·{" "}
                            <span className="rakam font-mono">
                              {a.yukleme_referansi}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {adaylar.data?.kesildi && (
                    <p className="m-0 mt-2 text-xs text-murekkep-3">
                      Yalnızca ilk {adayListesi.length} sonuç gösteriliyor;
                      aramayı daraltın.
                    </p>
                  )}
                </div>
              )}
            </SorguDurumu>
          </div>
        )}

        {kartUyarisi && (
          <div className="mt-3">
            <Uyari tur="uyari" baslik="Okutulan kart aktif değil">
              Kart bloke ya da iade edilmiş; öğrenci yine de bulundu. Yükleme
              öğrencinin cüzdanına yazılır, bu karta değil. Kartı Kartlar
              ekranından yenileyin.
            </Uyari>
          </div>
        )}

        {aranan.trim().length >= 3 && (
          <div className="mt-3">
            <SorguDurumu
              yukleniyor={referans.isPending}
              hata={referans.error}
              bos={!kisi}
              bosBaslik="Öğrenci bulunamadı"
              iskeletSatir={2}
              yenile={() => void referans.refetch()}
            >
              {kisi && (
                <div className="rounded-md border border-cetvel bg-kagit-2 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Avatar ad={kisi.ad} soyad={kisi.soyad} boyut={44} />
                      <div>
                        <p className="m-0 font-semibold text-murekkep">
                          {kisi.ad} {kisi.soyad}
                        </p>
                        <p className="m-0 text-xs text-murekkep-3">
                          {kisi.sinif ?? "—"}
                          {kisi.numara ? ` · No ${kisi.numara}` : ""} ·{" "}
                          <span className="rakam font-mono">
                            {kisi.yukleme_referansi}
                          </span>
                        </p>
                        {kisi.veliler.length > 0 && (
                          <p className="m-0 text-xs text-murekkep-3">
                            Veli:{" "}
                            {kisi.veliler
                              .map((v) => `${v.ad} ${v.soyad}`)
                              .join(", ")}{" "}
                            — ekstredeki gönderen adı genellikle budur.
                          </p>
                        )}
                      </div>
                    </div>
                    {kisi.bakiye && (
                      <div className="text-right">
                        <p className="m-0 text-xs text-murekkep-3">
                          Mevcut bakiye
                        </p>
                        <Tutar
                          deger={kisi.bakiye}
                          yonsuz
                          boy="lg"
                          etiket="mevcut bakiye"
                        />
                      </div>
                    )}
                  </div>

                  {!kisi.aktif && (
                    <p className="m-0 mt-2">
                      <Rozet tur="uyari">pasif öğrenci</Rozet>
                    </p>
                  )}

                  {kisi.bekleyen_bildirimler.length > 0 && (
                    <div className="mt-3 border-t border-cetvel pt-3">
                      <p className="m-0 mb-2 text-sm font-semibold text-murekkep-2">
                        Bu öğrencinin bekleyen bildirimleri
                      </p>
                      <ul className="m-0 flex list-none flex-col gap-1 p-0">
                        {kisi.bekleyen_bildirimler.map((b) => (
                          <li key={b.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setBildirimId(b.id);
                                setTutar(b.tutar);
                              }}
                              aria-pressed={bildirimId === b.id}
                              className={`flex min-h-hedef w-full items-center justify-between gap-3 rounded-md border px-3 text-left ${
                                bildirimId === b.id
                                  ? "border-simit bg-simit-yumusak"
                                  : "border-cetvel bg-yuzey hover:bg-kagit-3"
                              }`}
                            >
                              <span className="text-sm text-murekkep-2">
                                {tarihGoster(b.havale_tarihi)} tarihli bildirim
                              </span>
                              <Tutar deger={b.tutar} yonsuz boy="sm" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <p className="m-0 mt-2 text-xs text-murekkep-3">
                        Bir bildirim seçerseniz ekstre ile veli bildirimi çapraz
                        doğrulanır; tutar ve öğrenci uyuşmazsa işlem reddedilir.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </SorguDurumu>
          </div>
        )}
      </Kart>

      <Kart baslik="2 · Yüklemeyi işle">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setGonderildi(true);
            if (!kisi || !paraGecerliMi(tutar)) return;
            if (tip === "nakit" && !seciliNokta) return;
            // Onay anında para hareketi oluşur; bildirim
            // onayındaki gibi ikinci adımda özet gösterilip onaylanır.
            setOnayAcik(true);
          }}
        >
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1 p-0 text-sm font-semibold text-murekkep-2">
              Yükleme tipi
            </legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="yukleme-tipi"
                checked={tip === "havale"}
                onChange={() => setTip("havale")}
                className="h-4 w-4 accent-simit"
              />
              Havale / EFT (banka hesabına gelen)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="yukleme-tipi"
                checked={tip === "nakit"}
                onChange={() => setTip("nakit")}
                className="h-4 w-4 accent-simit"
              />
              Elden nakit tahsilat
            </label>
          </fieldset>

          <div className="grid gap-3 md:grid-cols-3">
            <Alan
              etiket="Tutar"
              value={tutar}
              onChange={(e) => setTutar(e.target.value)}
              inputMode="decimal"
              disabled={bildirimId !== null}
              ipucu={
                bildirimId
                  ? "Seçili bildirimin tutarı otoritedir; değiştirilemez."
                  : undefined
              }
              {...(tutarHatasi ? { hata: tutarHatasi } : {})}
            />
            {tip === "havale" ? (
              <Alan
                etiket="Havale tarihi (ekstredeki)"
                type="date"
                value={havaleTarihi}
                onChange={(e) => setHavaleTarihi(e.target.value)}
                disabled={bildirimId !== null}
              />
            ) : (
              <NakitNoktasiSecimi
                noktalar={noktalar.data?.noktalar ?? []}
                deger={noktaId}
                degistir={setNoktaId}
                {...(noktaHatasi ? { hata: noktaHatasi } : {})}
              />
            )}
          </div>

          {tip === "nakit" && (
            <Uyari tur="uyari" baslik="Nakit gün sonu sayımına girer">
              Nakit tahsilat, seçilen noktanın açık vardiyasına bağlanır ve
              kapanış sayımında beklenen nakde eklenir. Nokta seçilmezse işlem
              yapılamaz.
            </Uyari>
          )}

          <MetinAlani
            etiket="Açıklama"
            value={aciklama}
            onChange={(e) => setAciklama(e.target.value)}
            rows={2}
          />

          {mukerrerUyarisi && (
            <div className="rounded-lg border border-uyari-cizgi bg-uyari-yumusak p-3">
              <p className="m-0 font-semibold text-murekkep">
                Mükerrer yükleme koruması
              </p>
              <p className="m-0 mt-1 text-sm text-murekkep-2">
                {hataMetni(isle.error)} Çift yükleme, eksik yüklemeden daha
                pahalıdır; devam etmek için açıkça onaylayın.
              </p>
              <div className="mt-2">
                <OnayKutusu
                  etiket="Bu mükerrer bir kayıt değil, yüklemeyi yine de yap"
                  isaretli={tekrarOnayi}
                  degistir={setTekrarOnayi}
                />
              </div>
            </div>
          )}

          {!mukerrerUyarisi && <IslemDurumu hata={isle.error} />}

          {isle.data && (
            <Uyari tur="onay" baslik="Yükleme işlendi">
              <p className="m-0">
                {isle.data.kisi_ad} · {isle.data.tip_adi} ·{" "}
                <Tutar deger={isle.data.tutar} yonsuz boy="sm" /> · Yeni bakiye:{" "}
                <Tutar
                  deger={isle.data.yeni_bakiye}
                  yonsuz
                  boy="sm"
                  etiket="yeni bakiye"
                />
              </p>
              {isle.data.uyari && (
                <p className="m-0 mt-1 text-uyari">{isle.data.uyari}</p>
              )}
            </Uyari>
          )}

          {onayAcik && kisi ? (
            <TehlikeliOnay
              soru={`${kisi.ad} ${kisi.soyad} bakiyesine ${paraGoster(
                paraNormalize(tutar),
              )} yüklensin mi?`}
              aciklama="Bakiye anında artar ve veliye bildirim gider. Aşağıdaki özeti kontrol edin."
              onayMetni="Evet, yüklemeyi işle"
              onayla={() => isle.mutate()}
              vazgec={() => setOnayAcik(false)}
              yukleniyor={isle.isPending}
              cocuk={
                <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  <dt className="text-murekkep-3">Öğrenci</dt>
                  <dd className="m-0 text-murekkep">
                    {kisi.ad} {kisi.soyad} · {kisi.sinif ?? "—"}
                    {kisi.numara ? ` · No ${kisi.numara}` : ""}
                  </dd>
                  <dt className="text-murekkep-3">Yükleme tipi</dt>
                  <dd className="m-0 text-murekkep">
                    {tip === "nakit"
                      ? "Elden nakit tahsilat"
                      : "Havale / EFT"}
                  </dd>
                  <dt className="text-murekkep-3">
                    {tip === "nakit" ? "Nokta" : "Havale tarihi"}
                  </dt>
                  <dd className="m-0 text-murekkep">
                    {tip === "nakit"
                      ? (seciliNokta?.ad ?? "—")
                      : tarihGoster(havaleTarihi)}
                  </dd>
                  {bildirimId && (
                    <>
                      <dt className="text-murekkep-3">Veli bildirimi</dt>
                      <dd className="m-0 text-murekkep">
                        Seçili bildirimle çapraz doğrulanacak
                      </dd>
                    </>
                  )}
                  <dt className="text-murekkep-3">Tutar</dt>
                  <dd className="m-0 rakam font-mono text-murekkep">
                    {paraGoster(paraNormalize(tutar))}
                  </dd>
                  {kisi.bakiye && (
                    <>
                      <dt className="text-murekkep-3">Bakiye</dt>
                      <dd className="m-0 rakam font-mono text-murekkep">
                        {paraGoster(kisi.bakiye)}
                        {beklenenBakiye ? ` → ${beklenenBakiye}` : ""}
                      </dd>
                    </>
                  )}
                  {tip === "nakit" && (
                    <>
                      <dt className="text-murekkep-3">Gün sonu</dt>
                      <dd className="m-0 text-murekkep-2">
                        Nakit, noktanın açık vardiyasına bağlanır ve kapanış
                        sayımında beklenen nakde eklenir.
                      </dd>
                    </>
                  )}
                </dl>
              }
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Buton type="submit" tur="birincil" disabled={!kisi}>
                Yüklemeyi işle
              </Buton>
              {!kisi && (
                <span className="text-sm text-murekkep-3">
                  Önce öğrenciyi bulun: referans kodu, ad/okul no ya da kart.
                </span>
              )}
            </div>
          )}
        </form>
      </Kart>
    </>
  );
}

/**
 * Kart okutma alanı. USB HID okuyucu UID'i yazıp ENTER basar; Enter burada
 * durdurulur ki dış form gönderilmesin (ortak.tsx · BarkodAlani ile aynı
 * refleks). Alan her okutmada temizlenir, odak alanda kalır.
 */
function KartOkutmaAlani({
  deger,
  degistir,
  okut,
}: {
  deger: string;
  degistir: (yeni: string) => void;
  okut: (uid: string) => void;
}) {
  const alan = useRef<HTMLInputElement>(null);
  const gonder = () => {
    const uid = deger.trim();
    if (uid.length < 4) return;
    okut(uid);
    degistir("");
    alan.current?.focus();
  };
  return (
    <div className="grid gap-3 md:grid-cols-[1fr_auto]">
      <Alan
        ref={alan}
        etiket="Kart okut"
        value={deger}
        onChange={(e) => degistir(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          gonder();
        }}
        ipucu="Alanı seçip kartı okuyucuya okutun; UID elle de yazılabilir."
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        className="font-mono"
      />
      <div className="flex items-end">
        <Buton
          type="button"
          tur="ikincil"
          onClick={() => {
            if (deger.trim().length >= 4) gonder();
            else alan.current?.focus();
          }}
          title="Okuyucudan okutabilmek için alanı seçer"
        >
          Okuyucudan oku
        </Buton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function VerilenKararlar() {
  const [durum, setDurum] = useState(String(YuklemeBildirimDurumu.ONAYLANDI));
  const [arama, setArama] = useState("");
  const [sayfa, setSayfa] = useState(1);
  const sorgu = useQuery({
    queryKey: ["yukleme-bildirim", "karar", durum, arama, sayfa],
    queryFn: () =>
      apiOku<KuyrukYaniti>("/yukleme/bildirim", { durum, boyut: 50, arama, sayfa }),
  });

  const kayitlar = sorgu.data?.kayitlar ?? [];

  return (
    <Kart
      baslik="Verilen kararlar"
      ustBilgi={
        <div className="w-48">
          <Secim
            etiket="Durum"
            value={durum}
            onChange={(e) => { setDurum(e.target.value); setSayfa(1); }}
            secenekler={[
              {
                deger: String(YuklemeBildirimDurumu.ONAYLANDI),
                metin: "Onaylandı",
              },
              {
                deger: String(YuklemeBildirimDurumu.REDDEDILDI),
                metin: "Reddedildi",
              },
            ]}
          />
        </div>
      }
    >
      <Alan
        etiket="Öğrenci ara"
        placeholder="Ad, soyad, okul no veya yükleme referansı"
        value={arama}
        onChange={(e) => { setArama(e.target.value); setSayfa(1); }}
        maxLength={80}
      />
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={kayitlar.length === 0}
        bosBaslik="Kayıt yok"
        bosAciklama="Bu durumda bildirim bulunmuyor."
        yenile={() => void sorgu.refetch()}
      >
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {kayitlar.map((k) => (
            <li
              key={k.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-cetvel px-3 py-2"
            >
              <div>
                <p className="m-0 font-semibold text-murekkep">{k.kisi_ad}</p>
                <p className="m-0 text-xs text-murekkep-3">
                  #{k.id} · {k.sinif} · {k.yukleme_referansi} · {k.yukleme_tipi === HareketTipi.YUKLEME_NAKIT ? "Nakit" : "Havale"}
                </p>
                <p className="m-0 text-xs text-murekkep-3">
                  {tarihGoster(k.havale_tarihi)} · karar:{" "}
                  {tarihSaatGoster(k.karar_zamani)}
                  {k.onaylayan_ad ? ` · ${k.onaylayan_ad}` : ""} ·{" "}
                  {saatMetni(k.bekleme_saati)} sürdü
                </p>
                {k.red_gerekcesi && (
                  <p className="m-0 mt-1 text-sm text-cikan">
                    Gerekçe: {k.red_gerekcesi}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Rozet
                  tur={
                    k.durum === YuklemeBildirimDurumu.ONAYLANDI
                      ? "onay"
                      : "hata"
                  }
                >
                  {k.geri_alma_id
                    ? para(k.kalan_tutar).isZero() ? "Geri alındı" : "Kısmen geri alındı"
                    : k.durum_adi}
                </Rozet>
                <Tutar deger={k.tutar} yonsuz etiket="tutar" />
              </div>
              {k.durum === YuklemeBildirimDurumu.ONAYLANDI && (
                <YuklemeGeriAl kayit={k} />
              )}
            </li>
          ))}
        </ul>
      </SorguDurumu>
      <div className="mt-3 flex items-center gap-3">
        <Buton tur="ikincil" disabled={sayfa === 1 || sorgu.isFetching} onClick={() => setSayfa((s) => s - 1)}>Önceki</Buton>
        <span>Sayfa {sayfa}</span>
        <Buton tur="ikincil" disabled={kayitlar.length < 50 || sorgu.isFetching} onClick={() => setSayfa((s) => s + 1)}>Sonraki</Buton>
      </div>
    </Kart>
  );
}

/**
 * Dekont bağı.
 *
 * ONAY KARARININ DAYANAĞI BUDUR: personel dekonta bakmadan onaylarsa, "para
 * gelmediği hâlde bakiye arttı" senaryosu gerçekleşir. Bu yüzden dosya
 * açılamadığında sessiz kalınmaz — hata görünür şekilde yazılır.
 *
 * Dosya yeni bir sekmede AÇILMAZ, indirilir: sunucu dekontu `attachment`
 * olarak veriyor (PDF'in panelin kökeninde çalıştırılmaması için) ve içerik
 * yalnızca yetkili bir istekle alınabildiği için ortada açılabilecek bir adres
 * de yok — `blob:` adresi bu sekmeye ait geçici bir kopyadır.
 */
function DekontBagi({ kayit }: { kayit: KuyrukSatiri }) {
  const goruntu = useMedyaGoruntusu(kayit.dekont_medya_id, kayit.dekont_url);

  if (!kayit.dekont_medya_id && !kayit.dekont_url) {
    return <span className="text-xs text-murekkep-3">Dekont eklenmemiş</span>;
  }
  if (goruntu.hata) {
    return (
      <span role="alert" className="text-xs text-hata">
        Dekont açılamadı: {goruntu.hata}
      </span>
    );
  }
  if (!goruntu.url) {
    return <span className="text-xs text-murekkep-3">Dekont yükleniyor…</span>;
  }
  return (
    <a
      href={goruntu.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-h-hedef items-center rounded-lg border border-cetvel bg-yuzey px-3 text-sm text-murekkep hover:bg-kagit-2"
    >
      Dekontu aç
    </a>
  );
}
